import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { guessTypeIcon, EXTRA_TYPE_COLORS, TYPE_ICONS } from "@/lib/portalConstants";
import { format, parseISO, addDays } from "date-fns";
import { Plus, Repeat } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = {
  title: "",
  type: "appointment",
  client_id: "none",
  date: format(new Date(), "yyyy-MM-dd"),
  start_time: "",
  end_time: "",
  notes: "",
  repeat_weeks: 0,
};

export function TaskDialog({ open, onOpenChange, clients, allTypes, customTypeCount = 0, editTask, initialDate, initialClientId, onTypesChanged, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [newTypeOpen, setNewTypeOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [savingType, setSavingType] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        editTask
          ? { ...empty, ...editTask, client_id: editTask.client_id || "none", repeat_weeks: 0 }
          : { ...empty, date: initialDate || format(new Date(), "yyyy-MM-dd"), client_id: initialClientId || "none" }
      );
    }
  }, [open, editTask, initialDate, initialClientId]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please add a title.");
    const payload = { ...form, client_id: form.client_id === "none" ? null : form.client_id };
    setSaving(true);
    try {
      if (editTask) {
        delete payload.repeat_weeks;
        await api.put(`/portal/tasks/${editTask.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/portal/tasks", payload);
        toast.success(payload.repeat_weeks > 1 ? `Added ${payload.repeat_weeks} weekly visits` : "Task added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save the task.");
    }
    setSaving(false);
  };

  const addType = async () => {
    const label = newTypeName.trim();
    if (!label) return toast.error("Give the category a name.");
    setSavingType(true);
    try {
      const res = await api.post("/portal/task-types", { label, icon: guessTypeIcon(label) });
      toast.success(`"${label}" added with its own color`);
      setNewTypeOpen(false);
      setNewTypeName("");
      await onTypesChanged?.();
      setForm((f) => ({ ...f, type: res.data.value }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add that category.");
    }
    setSavingType(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-sand max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editTask ? "Edit Task" : "Add Task"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Morning care visit — Margaret" className="mt-1.5 bg-white h-11" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => (v === "__new__" ? setNewTypeOpen(true) : setForm({ ...form, type: v }))}>
                  <SelectTrigger data-testid="task-type" className="mt-1.5 bg-white h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="inline-flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__" data-testid="task-type-new">
                      <span className="inline-flex items-center gap-2 text-sage-700 font-medium">
                        <Plus className="h-3.5 w-3.5" />
                        New category…
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client (optional)</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="task-client" className="mt-1.5 bg-white h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Date</Label>
                <Input type="date" data-testid="task-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 bg-white h-11" />
              </div>
              <div>
                <Label>Start</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1.5 bg-white h-11" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1.5 bg-white h-11" />
              </div>
            </div>
            {!editTask && (
              <div>
                <Label className="inline-flex items-center gap-1.5"><Repeat className="h-3.5 w-3.5 text-sage" /> Repeats</Label>
                <Select value={String(form.repeat_weeks)} onValueChange={(v) => setForm({ ...form, repeat_weeks: Number(v) })}>
                  <SelectTrigger data-testid="task-repeat" className="mt-1.5 bg-white h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Does not repeat</SelectItem>
                    <SelectItem value="4">Every week — 4 visits</SelectItem>
                    <SelectItem value="8">Every week — 8 visits</SelectItem>
                    <SelectItem value="12">Every week — 12 visits</SelectItem>
                  </SelectContent>
                </Select>
                {form.repeat_weeks > 1 && (
                  <p className="text-xs text-charcoal-soft mt-1.5">
                    This will add a visit every week on {format(parseISO(form.date || format(new Date(), "yyyy-MM-dd")), "EEEE")}s, through {format(addDays(parseISO(form.date || format(new Date(), "yyyy-MM-dd")), (form.repeat_weeks - 1) * 7), "MMMM d, yyyy")}.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} data-testid="save-task" className="w-full rounded-full bg-sage text-white py-3.5 font-medium hover:bg-sage-dark transition-colors disabled:opacity-60">
              {saving ? "Saving…" : editTask ? "Save Changes" : form.repeat_weeks > 1 ? `Add ${form.repeat_weeks} Weekly Visits` : "Add Task"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newTypeOpen} onOpenChange={setNewTypeOpen}>
        <DialogContent className="bg-sand max-w-sm" data-testid="new-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New category</DialogTitle>
          </DialogHeader>
          <p className="text-charcoal-soft text-sm">
            Name it once — it gets its own color and icon everywhere, including the calendar legend. Anything without its own category can simply stay “Other”.
          </p>
          <div className="mt-2">
            <Label>Name</Label>
            <Input
              data-testid="new-category-name"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="e.g. Medication Assistance"
              className="mt-1.5 bg-white h-11"
              onKeyDown={(e) => e.key === "Enter" && addType()}
            />
            {newTypeName.trim() && (() => {
              const c = EXTRA_TYPE_COLORS[customTypeCount % EXTRA_TYPE_COLORS.length];
              const Icon = TYPE_ICONS[guessTypeIcon(newTypeName)] || TYPE_ICONS.sparkles;
              return (
                <p className="text-xs text-charcoal-soft mt-2 flex items-center gap-2 flex-wrap">
                  It will look like:
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${c.color}`} data-testid="new-category-preview">
                    <Icon className="h-3 w-3" />
                    {newTypeName.trim()}
                  </span>
                </p>
              );
            })()}
          </div>
          <DialogFooter>
            <button onClick={addType} disabled={savingType} data-testid="save-category-button" className="w-full rounded-full bg-sage text-white py-3 font-medium hover:bg-sage-dark transition-colors disabled:opacity-60">
              {savingType ? "Adding…" : "Add Category"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
